import React, { useState, useEffect } from "react";
import Select from "react-select";
import {
  DatePicker,
  TimePicker,
  Form,
  Input,
  Radio,
  Button,
  Modal,
  Spin,
  Select as AntSelect,
} from "antd";
import { MdCloudUpload, MdEdit, MdEventNote, MdEvent } from "react-icons/md";
import toast from "react-hot-toast";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useAtom } from "jotai";
import { gbState } from "../../../components/shared/GlobalState";
import { ArrowRightIcon, CalendarIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import EventCardList from "./EventCardList";

// Extend dayjs with the isBetween plugin
dayjs.extend(isBetween);
dayjs.extend(timezone);
dayjs.extend(utc);

const EventUpdatePageChild = ({ currentRecord }) => {
  let navigate = useNavigate();
  const [{ errorLogApi, user }] = useAtom(gbState);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [venueListOptions, setVenueListOptions] = useState([]);

  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(
    currentRecord?.event_start_date
      ? dayjs(currentRecord.event_start_date)
      : null
  );
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [startTimeSelected, setStartTimeSelected] = useState(false);

  // Add these options for the dropdowns
  const sponsorshipOptions = [
    { value: "Open", label: "Open" },
    { value: "Closed", label: "Closed" },
  ];

  const eventTypeOptions = [
    { value: "Public", label: "Public" },
    { value: "Private", label: "Private" },
  ];

  useEffect(() => {
    let userToken = localStorage.getItem("token");

    let urlLink = `${process.env.REACT_APP_API_URL}/venues/${user.church_id}`;

    const config = {
      method: "GET",
      url: urlLink,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };
    axios(config)
      .then(function (response) {
        const oldData = response.data.venues;
        const updatedData = oldData.map((data) => {
          return {
            value: data._id,
            label: data.venue_name,
          };
        });

        setVenueListOptions(updatedData);
      })
      .catch(function (err) {
        console.log("error api call ", err.response.data.message);
      });
  }, [user.church_id, errorLogApi]);

  useEffect(() => {
    const getEventListDataFromServer = async () => {
      setIsLoadingEvents(true);
      try {
        let userToken = localStorage.getItem("token");
        let urlLink = `${process.env.REACT_APP_API_URL}/event/zipcode/${user.church_zipcode}`;

        const response = await axios({
          method: "GET",
          url: urlLink,
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        });

        const formattedEvents = response.data.events.map((event) => ({
          ...event,
          event_start_date: dayjs(event.event_start_date).format("YYYY-MM-DD"),
          event_end_date: dayjs(event.event_end_date).format("YYYY-MM-DD"),
        }));

        setEvents(formattedEvents);

        // If there's a selected date, filter events for that date
        if (selectedDate) {
          const selectedDateStr = selectedDate.format("YYYY-MM-DD");
          const eventsOnSelectedDate = formattedEvents.filter((event) => {
            const eventStartDate = dayjs(event.event_start_date);
            const eventEndDate = dayjs(event.event_end_date);
            const selectedDayjs = dayjs(selectedDateStr);

            return selectedDayjs.isBetween(
              eventStartDate,
              eventEndDate,
              "day",
              "[]"
            );
          });
          setFilteredEvents(eventsOnSelectedDate);
        }

        console.log("Formatted events:", formattedEvents);
      } catch (err) {
        console.error("Error fetching events:", err);
        toast.error("Failed to fetch events");
        errorLogApi(err);
      } finally {
        setIsLoadingEvents(false);
      }
    };

    getEventListDataFromServer();
  }, [user.church_zipcode, errorLogApi, selectedDate]);

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setSelectedImage(file);
    }
  };

  const handleDateSelect = (date) => {
    if (!date) {
      setSelectedDate(null);
      setFilteredEvents([]);
      return;
    }

    setSelectedDate(date);
    const selectedDateStr = date.format("YYYY-MM-DD");

    // Filter events that overlap with the selected date
    const eventsOnSelectedDate = events.filter((event) => {
      const eventStartDate = dayjs(event.event_start_date);
      const eventEndDate = dayjs(event.event_end_date);
      const selectedDayjs = dayjs(selectedDateStr);

      // Check if selected date falls between event start and end dates (inclusive)
      return selectedDayjs.isBetween(eventStartDate, eventEndDate, "day", "[]");
    });

    console.log("Selected date:", selectedDateStr);
    console.log("Filtered events:", eventsOnSelectedDate);
    setFilteredEvents(eventsOnSelectedDate);
  };

  const handleEvent = async (values) => {
    try {
      setLoading(true);
      console.log("Form values before processing:", values);

      if (!values.title || !values.description) {
        toast.error("Please fill in all required fields");
        setLoading(false);
        return;
      }

      const userToken = localStorage.getItem("token");
      if (!userToken) {
        toast.error("Authentication token missing");
        navigate("/login");
        setLoading(false);
        return;
      }

      const formDataToSend = new FormData();

      // Add basic fields
      formDataToSend.append("title", values.title);
      formDataToSend.append("description", values.description);
      formDataToSend.append("day_event", values.day_event ? "1" : "0");
      formDataToSend.append(
        "event_contact_number",
        values.event_contact_number
      );
      formDataToSend.append("event_location", values.event_location);
      formDataToSend.append("church_id", user?.church_id);
      formDataToSend.append("createdBy", user?._id);
      formDataToSend.append("time_zone", user?.time_zone);

      // Convert dates to the user's timezone before sending
      if (values.event_start_date) {
        const startDate = dayjs(values.event_start_date)
          .tz(user?.time_zone, true)
          .format("YYYY-MM-DD");
        formDataToSend.append("event_start_date", startDate);
      }

      if (values.event_end_date) {
        const endDate = dayjs(values.event_end_date)
          .tz(user?.time_zone, true)
          .format("YYYY-MM-DD");
        formDataToSend.append("event_end_date", endDate);
      }

      // Convert times to the user's timezone before sending
      if (values.event_start_time) {
        const startTime = dayjs(values.event_start_time)
          .tz(user?.time_zone, true)
          .format("HH:mm:ss");
        formDataToSend.append("event_start_time", startTime);
      }

      if (values.event_end_time) {
        const endTime = dayjs(values.event_end_time)
          .tz(user?.time_zone, true)
          .format("HH:mm:ss");
        formDataToSend.append("event_end_time", endTime);
      }

      // Add event type and sponsorship status
      if (values.event_type) {
        formDataToSend.append(
          "event_type",
          JSON.stringify({
            value: values.event_type.value,
            label: values.event_type.label,
          })
        );
      }
      if (values.sponsorship_status) {
        formDataToSend.append(
          "sponsorship_status",
          JSON.stringify({
            value: values.sponsorship_status.value,
            label: values.sponsorship_status.label,
          })
        );
      }

      // Add image if selected
      if (selectedImage) {
        formDataToSend.append("image", selectedImage);
      }

      // Log the FormData entries for debugging
      for (let pair of formDataToSend.entries()) {
        console.log(pair[0], pair[1]);
      }

      const config = {
        headers: {
          Authorization: `Bearer ${userToken}`,
          "Content-Type": "multipart/form-data",
        },
      };

      const baseUrl = process.env.REACT_APP_API_URL;
      const endpoint = currentRecord?._id
        ? `${baseUrl}/event/update/${currentRecord._id}`
        : `${baseUrl}/event/add`;

      const response = await axios.post(endpoint, formDataToSend, config);
      console.log("Server response:", response.data);

      toast.success(
        currentRecord
          ? "Event updated successfully"
          : "Event created successfully"
      );
      navigate("/events");
    } catch (error) {
      console.error("Error submitting form:", error);
      let errorMessage = "An unexpected error occurred";

      if (error.response) {
        errorMessage =
          error.response.data.message ||
          error.response.data.error ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = "No response from server. Please check your connection.";
      } else {
        errorMessage = error.message || "Failed to make request";
      }

      toast.error(errorMessage);
      errorLogApi(error);
    } finally {
      setLoading(false);
    }
  };

  const showEventDetails = (event) => {
    setSelectedEvent(event);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedEvent(null);
  };

  const EventsList = () => {
    if (isLoadingEvents) {
      return (
        <div className="flex items-center justify-center w-full h-24 sm:h-32">
          <Spin />
        </div>
      );
    }

    if (!selectedDate) {
      return (
        <div className="flex flex-col items-center justify-center w-full h-24 text-gray-500 sm:h-32">
          <MdEvent className="w-6 h-6 mb-2 sm:w-8 sm:h-8" />
          <p className="text-sm text-center sm:text-base">
            Select a date to view events
          </p>
        </div>
      );
    }

    const formattedDate = dayjs(selectedDate).format("MMMM D, YYYY");
    const selectedDateStr = dayjs(selectedDate).format("YYYY-MM-DD");

    if (filteredEvents.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center w-full h-24 text-gray-500 sm:h-32">
          <MdEvent className="w-6 h-6 mb-2 sm:w-8 sm:h-8" />
          <p className="text-sm text-center sm:text-base">
            No events scheduled for {formattedDate}
          </p>
          <p className="mt-1 text-xs text-gray-400 sm:text-sm">
            Selected date: {selectedDateStr}
          </p>
        </div>
      );
    }

    return (
      <EventCardList
        filteredEvents={filteredEvents}
        showEventDetails={showEventDetails}
      />
    );
  };

  // Add this function to validate dates
  const validateEndDate = (_, value) => {
    const startDate = form.getFieldValue("event_start_date");
    if (startDate && value && value.isBefore(startDate)) {
      return Promise.reject("End date cannot be before start date");
    }
    return Promise.resolve();
  };

  // Add this function to validate end time
  const disabledEndTime = () => {
    const startDate = form.getFieldValue("event_start_date");
    const endDate = form.getFieldValue("event_end_date");
    const startTime = form.getFieldValue("event_start_time");

    if (!startTime) {
      return {};
    }

    // Only apply restrictions if dates are the same
    if (startDate && endDate && startDate.isSame(endDate, "day")) {
      return {
        disabledHours: () => {
          const hours = [];
          for (let i = 0; i < startTime.hour(); i++) {
            hours.push(i);
          }
          return hours;
        },
        disabledMinutes: (selectedHour) => {
          if (selectedHour === startTime.hour()) {
            const minutes = [];
            for (let i = 0; i < startTime.minute(); i++) {
              minutes.push(i);
            }
            return minutes;
          }
          return [];
        },
      };
    }
    return {};
  };

  return (
    <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6">
      <div className="flex flex-col w-full gap-6 lg:flex-row">
        <div className="w-full lg:w-2/3 lg:min-w-[700px]">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleEvent}
            initialValues={{
              ...currentRecord,
              event_start_date: currentRecord?.event_start_date
                ? dayjs(currentRecord.event_start_date)
                : null,
              event_end_date: currentRecord?.event_end_date
                ? dayjs(currentRecord.event_end_date)
                : null,
              event_start_time: currentRecord?.event_start_time
                ? dayjs(currentRecord.event_start_time, "HH:mm:ss")
                : null,
              event_end_time: currentRecord?.event_end_time
                ? dayjs(currentRecord.event_end_time, "HH:mm:ss")
                : null,
              day_event: currentRecord?.day_event ? 1 : 0,
            }}
            className="space-y-6"
          >
            <div className="flex flex-col items-center justify-between gap-4 pb-6 border-b border-gray-200 sm:flex-row">
              <div className="flex items-center gap-3">
                <MdEventNote className="w-6 h-6 text-blue-500 sm:w-8 sm:h-8" />
                <h2 className="text-xl font-bold text-gray-800 sm:text-2xl">
                  {currentRecord ? "Edit Event" : "Add Event"}
                </h2>
                <div className="flex items-center text-sm text-gray-500">
                  <CalendarIcon className="w-4 h-4 mr-1" />
                  <span>{dayjs().format("MMMM D, YYYY")}</span>
                  <span className="mx-2">•</span>
                  <span>{dayjs().tz(user?.time_zone).format("h:mm A")}</span>
                  <span className="mx-2">•</span>
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full text-xs font-medium">
                    {user?.time_zone?.replace(/_/g, " ")}
                  </span>
                </div>
              </div>

              <Button
                key="submit"
                type="primary"
                htmlType="submit"
                size="large"
                className="flex items-center justify-center w-full gap-2 px-4 py-2 text-white transition-all bg-blue-500 rounded-md sm:w-auto hover:bg-blue-600 disabled:opacity-50"
                loading={loading}
                disabled={loading}
                onClick={() => form.submit()}
              >
                {loading ? (
                  "Saving..."
                ) : (
                  <div className="flex items-center gap-2">
                    <MdEdit className="w-4 h-4" />
                    <span>
                      {currentRecord ? "Update Event" : "Create Event"}
                    </span>
                  </div>
                )}
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <Form.Item
                name="event_start_date"
                label={
                  <span className="font-medium text-gray-700">Start Date</span>
                }
                rules={[
                  { required: true, message: "Please select the start date" },
                ]}
              >
                <DatePicker
                  className="w-full h-[38px] border-gray-300 rounded-md"
                  onChange={(date) => {
                    handleDateSelect(date);
                    if (date) {
                      // Set end date to same as start date by default
                      const tzDate = dayjs(date).tz(user?.time_zone);
                      form.setFieldsValue({
                        event_end_date: tzDate,
                        // Reset times when date changes
                        event_start_time: null,
                        event_end_time: null,
                      });
                    }
                  }}
                  disabledDate={(current) =>
                    current &&
                    current.isBefore(dayjs().tz(user?.time_zone), "day")
                  }
                  defaultValue={dayjs().tz(user?.time_zone)}
                  format="MM-DD-YYYY"
                />
              </Form.Item>

              <Form.Item
                name="event_end_date"
                label={
                  <span className="font-medium text-gray-700">End Date</span>
                }
                rules={[
                  { required: true, message: "Please select the end date" },
                  { validator: validateEndDate },
                ]}
              >
                <DatePicker
                  className="w-full h-[38px] border-gray-300 rounded-md"
                  disabledDate={(current) => {
                    const startDate = form.getFieldValue("event_start_date");
                    if (!startDate) return false;
                    return current && current.isBefore(startDate, "day");
                  }}
                  disabled={!form.getFieldValue("event_start_date")}
                  format="MM-DD-YYYY"
                />
              </Form.Item>

              <Form.Item
                name="event_start_time"
                label={
                  <span className="font-medium text-gray-700">Start Time</span>
                }
                rules={[
                  { required: true, message: "Please select the start time" },
                ]}
              >
                <TimePicker
                  className="w-full h-[38px] border-gray-300 rounded-md"
                  popupClassName="custom-time-picker-dropdown"
                  format="HH:mm:ss"
                  showNow={true}
                  onChange={(time) => {
                    setStartTimeSelected(!!time);
                    if (time) {
                      const tzTime = dayjs(time).tz(user?.time_zone);
                      form.setFieldsValue({
                        event_start_time: tzTime,
                      });
                      // Reset end time if it's before new start time
                      const endTime = form.getFieldValue("event_end_time");
                      if (endTime && endTime.isBefore(tzTime)) {
                        form.setFieldsValue({
                          event_end_time: null,
                        });
                      }
                    }
                  }}
                />
              </Form.Item>

              <Form.Item
                name="event_end_time"
                label={
                  <span className="font-medium text-gray-700">End Time</span>
                }
                rules={[
                  { required: true, message: "Please select the end time" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const startTime = getFieldValue("event_start_time");
                      const startDate = getFieldValue("event_start_date");
                      const endDate = getFieldValue("event_end_date");

                      if (!startTime || !value) {
                        return Promise.resolve();
                      }

                      // Only validate time if dates are the same
                      if (
                        startDate &&
                        endDate &&
                        startDate.isSame(endDate, "day")
                      ) {
                        const tzStartTime = dayjs(startTime).tz(
                          user?.time_zone
                        );
                        const tzEndTime = dayjs(value).tz(user?.time_zone);
                        if (tzEndTime.isBefore(tzStartTime)) {
                          return Promise.reject(
                            "End time must be after start time on the same day"
                          );
                        }
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <TimePicker
                  className="w-full h-[38px] border-gray-300 rounded-md"
                  format="HH:mm:ss"
                  showNow={true}
                  disabled={!startTimeSelected}
                  popupClassName="custom-time-picker-dropdown"
                  disabledTime={disabledEndTime}
                />
              </Form.Item>
            </div>

            <div className="flex flex-col items-center p-6 border-2 border-gray-300 border-dashed rounded-md">
              {imagePreview ? (
                <div className="relative group">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="object-cover w-40 h-40 rounded-md shadow-md"
                  />
                  <div className="absolute inset-0 flex items-center justify-center transition-opacity rounded-md opacity-0 bg-black/50 group-hover:opacity-100">
                    <label
                      htmlFor="image-upload"
                      className="px-4 py-2 text-sm text-white cursor-pointer"
                    >
                      Change Image
                    </label>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center w-full p-6 text-center">
                  <MdCloudUpload className="w-12 h-12 mb-2 text-gray-400" />
                  <label
                    htmlFor="image-upload"
                    className="px-4 py-2 text-sm font-medium text-blue-500 transition-colors border-2 border-blue-500 rounded-md cursor-pointer hover:bg-blue-50"
                  >
                    Select Image
                  </label>
                  <p className="mt-2 text-sm text-gray-500">
                    PNG, JPG or GIF (max. 5MB)
                  </p>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
                id="image-upload"
              />
            </div>

            <div className="grid gap-6">
              <Form.Item
                name="title"
                label={
                  <span className="font-medium text-gray-700">Event Title</span>
                }
                rules={[
                  { required: true, message: "Please enter the event title" },
                ]}
              >
                <Input
                  placeholder="Enter event title"
                  className="h-[38px] border-gray-300 rounded-md focus:border-blue-500 focus:ring-blue-500"
                />
              </Form.Item>

              <Form.Item
                name="description"
                label={
                  <span className="font-medium text-gray-700">Description</span>
                }
                rules={[
                  {
                    required: true,
                    message: "Please enter the event description",
                  },
                ]}
              >
                <Input.TextArea
                  rows={4}
                  placeholder="Enter event description"
                  className="border-gray-300 rounded-md focus:border-blue-500 focus:ring-blue-500"
                />
              </Form.Item>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                <Form.Item
                  name="day_event"
                  label={
                    <span className="font-medium text-gray-700">Day Event</span>
                  }
                >
                  <Radio.Group className="flex gap-4">
                    <Radio value={1} className="text-gray-700">
                      Yes
                    </Radio>
                    <Radio value={0} className="text-gray-700">
                      No
                    </Radio>
                  </Radio.Group>
                </Form.Item>

                <Form.Item
                  name="sponsorship_status"
                  label={
                    <span className="font-medium text-gray-700">
                      Sponsorship Status
                    </span>
                  }
                  rules={[
                    {
                      required: true,
                      validator: (_, value) => {
                        if (!value || !value.value) {
                          return Promise.reject(
                            "Please select sponsorship status"
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Select
                    options={sponsorshipOptions}
                    className="w-full"
                    placeholder="Select sponsorship status"
                    labelInValue
                    onChange={(selected) => {
                      form.setFieldsValue({
                        sponsorship_status: {
                          value: selected.value,
                          label: selected.label,
                        },
                      });
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="event_type"
                  label={
                    <span className="font-medium text-gray-700">
                      Event Type
                    </span>
                  }
                  rules={[
                    {
                      required: true,
                      validator: (_, value) => {
                        if (!value || !value.value) {
                          return Promise.reject("Please select event type");
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Select
                    options={eventTypeOptions}
                    className="w-full"
                    placeholder="Select event type"
                    labelInValue
                    onChange={(selected) => {
                      form.setFieldsValue({
                        event_type: {
                          value: selected.value,
                          label: selected.label,
                        },
                      });
                    }}
                  />
                </Form.Item>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-2">
                <Form.Item
                  name="event_contact_number"
                  label={
                    <span className="font-medium text-gray-700">
                      Contact Number
                    </span>
                  }
                  rules={[
                    { required: true, message: "Please enter contact number" },
                  ]}
                >
                  <Input
                    placeholder="Enter contact number"
                    className="w-full h-[38px] border-gray-300 rounded-md"
                  />
                </Form.Item>

                <Form.Item
                  name="event_location"
                  label={
                    <span className="font-medium text-gray-700">
                      Event Location
                    </span>
                  }
                  rules={[
                    { required: true, message: "Please enter event location" },
                  ]}
                >
                  <AntSelect
                    placeholder="Select venue"
                    options={venueListOptions}
                    onChange={(value, option) => {
                      // Find the selected option to get its label
                      const selectedOption = venueListOptions.find(
                        (opt) => opt.value === value
                      );
                      if (selectedOption) {
                        form.setFieldsValue({
                          event_location: selectedOption.label,
                        });
                        console.log("Selected venue:", selectedOption.label);
                      }
                    }}
                    className="w-full h-[38px]"
                  >
                    {venueListOptions.map((option) => (
                      <AntSelect.Option key={option.value} value={option.value}>
                        {option.label}
                      </AntSelect.Option>
                    ))}
                  </AntSelect>
                  {/* <Input
                    placeholder="Enter event location"
                    className="w-full h-[38px] border-gray-300 rounded-md"
                  /> */}
                </Form.Item>
              </div>
            </div>
            {/* {JSON.stringify(form.getFieldsValue("event_type"))} */}
          </Form>
        </div>

        <div className="w-full lg:w-1/3 lg:min-w-[300px] lg:max-w-[350px] p-4 bg-white rounded-lg shadow-md sm:p-6">
          <div className="sticky top-4">
            <h3 className="flex items-center gap-2 mb-4 text-lg font-semibold text-gray-900">
              <MdEvent className="w-5 h-5 text-blue-500" />
              Events on Selected Date
            </h3>
            <EventsList />
          </div>
        </div>
      </div>

      <Modal
        title={
          <div className="flex items-center gap-2">
            <MdEventNote className="w-6 h-6 text-blue-500" />
            <span className="text-xl font-semibold">
              {selectedEvent?.title}
            </span>
          </div>
        }
        visible={isModalVisible}
        onCancel={handleModalClose}
        footer={[
          <Button
            key="close"
            onClick={handleModalClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </Button>,
        ]}
        width="90%"
        maxWidth={600}
        className="rounded-lg"
        centered
      >
        {selectedEvent && (
          <div className="space-y-4">
            {selectedEvent.image && (
              <div className="mb-6">
                <div className="relative w-full h-48 overflow-hidden rounded-md">
                  <a
                    href={`${process.env.REACT_APP_REPORTS_DOWNLOAD_URL}uploads/event/${selectedEvent.image}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      crossOrigin="anonymous"
                      src={`${process.env.REACT_APP_REPORTS_DOWNLOAD_URL}uploads/event/${selectedEvent.image}`}
                      alt={selectedEvent.title}
                      className="object-cover w-full h-full transition-opacity cursor-pointer hover:opacity-90"
                    />
                  </a>
                </div>
              </div>
            )}

            <div className="p-4 rounded-md bg-gray-50">
              <h4 className="mb-2 font-medium text-gray-700">Description</h4>
              <p className="text-gray-600">{selectedEvent.description}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-white border border-gray-100 rounded-md">
                <h4 className="mb-1 font-medium text-gray-700">Start Date</h4>
                <p className="text-gray-600">
                  {dayjs(selectedEvent.event_start_date).format("MMMM D, YYYY")}
                </p>
              </div>
              <div className="p-3 bg-white border border-gray-100 rounded-md">
                <h4 className="mb-1 font-medium text-gray-700">End Date</h4>
                <p className="text-gray-600">
                  {dayjs(selectedEvent.event_end_date).format("MMMM D, YYYY")}
                </p>
              </div>
              <div className="p-3 bg-white border border-gray-100 rounded-md">
                <h4 className="mb-1 font-medium text-gray-700">Start Time</h4>
                <p className="text-gray-600">
                  {dayjs(selectedEvent.event_start_time, "HH:mm:ss").format(
                    "hh:mm A"
                  )}
                </p>
              </div>
              <div className="p-3 bg-white border border-gray-100 rounded-md">
                <h4 className="mb-1 font-medium text-gray-700">End Time</h4>
                <p className="text-gray-600">
                  {dayjs(selectedEvent.event_end_time, "HH:mm:ss").format(
                    "hh:mm A"
                  )}
                </p>
              </div>
            </div>

            {selectedEvent.day_event && (
              <div className="p-3 mt-4 text-blue-700 rounded-md bg-blue-50">
                <p className="text-sm font-medium">This is a full-day event</p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EventUpdatePageChild;
